import KnowledgeBaseAPI from 'dashboard/api/captain/knowledgeBase';

const state = {
  records: [], // global list of KBs
  linkedByAssistant: {}, // assistantId => array of KBs linked to it
  documentsByKb: {}, // knowledgeBaseId => array of documents in it
  uiFlags: {
    fetching: false,
    fetchingLinked: false,
    fetchingDocuments: false,
    saving: false,
    deleting: false,
    linking: false,
  },
};

const getters = {
  getAll: s => s.records,
  getById: s => id => s.records.find(kb => kb.id === Number(id)),
  getLinked: s => assistantId => s.linkedByAssistant[assistantId] || [],
  getDocuments: s => kbId => s.documentsByKb[kbId] || [],
  getUIFlags: s => s.uiFlags,
};

const mutations = {
  SET(s, list) {
    s.records = list;
  },
  ADD(s, kb) {
    s.records = [...s.records, kb];
  },
  UPDATE(s, kb) {
    s.records = s.records.map(it => (it.id === kb.id ? kb : it));
  },
  REMOVE(s, id) {
    s.records = s.records.filter(it => it.id !== id);
  },
  SET_LINKED(s, { assistantId, list }) {
    s.linkedByAssistant = { ...s.linkedByAssistant, [assistantId]: list };
  },
  SET_DOCUMENTS(s, { kbId, list }) {
    s.documentsByKb = { ...s.documentsByKb, [kbId]: list };
  },
  SET_UI(s, flag) {
    s.uiFlags = { ...s.uiFlags, ...flag };
  },
};

const actions = {
  async fetch({ commit }) {
    commit('SET_UI', { fetching: true });
    try {
      const { data } = await KnowledgeBaseAPI.list();
      commit('SET', data);
      return data;
    } finally {
      commit('SET_UI', { fetching: false });
    }
  },

  async create({ commit }, payload) {
    commit('SET_UI', { saving: true });
    try {
      const { data } = await KnowledgeBaseAPI.create({ payload });
      commit('ADD', data);
      return data;
    } finally {
      commit('SET_UI', { saving: false });
    }
  },

  async update({ commit }, { id, payload }) {
    commit('SET_UI', { saving: true });
    try {
      const { data } = await KnowledgeBaseAPI.update({ id, payload });
      commit('UPDATE', data);
      return data;
    } finally {
      commit('SET_UI', { saving: false });
    }
  },

  async delete({ commit }, id) {
    commit('SET_UI', { deleting: true });
    try {
      await KnowledgeBaseAPI.delete({ id });
      commit('REMOVE', id);
    } finally {
      commit('SET_UI', { deleting: false });
    }
  },

  async fetchDocuments({ commit }, kbId) {
    commit('SET_UI', { fetchingDocuments: true });
    try {
      const { data } = await KnowledgeBaseAPI.documents({ id: kbId });
      commit('SET_DOCUMENTS', { kbId, list: data });
      return data;
    } finally {
      commit('SET_UI', { fetchingDocuments: false });
    }
  },

  async fetchLinked({ commit }, assistantId) {
    commit('SET_UI', { fetchingLinked: true });
    try {
      const { data } = await KnowledgeBaseAPI.listForAssistant({ assistantId });
      commit('SET_LINKED', { assistantId, list: data });
      return data;
    } finally {
      commit('SET_UI', { fetchingLinked: false });
    }
  },

  async link({ commit }, { assistantId, knowledgeBaseId }) {
    commit('SET_UI', { linking: true });
    try {
      const { data } = await KnowledgeBaseAPI.link({
        assistantId,
        knowledgeBaseId,
      });
      commit('SET_LINKED', { assistantId, list: data });
      return data;
    } finally {
      commit('SET_UI', { linking: false });
    }
  },

  async unlink({ commit, state: s }, { assistantId, knowledgeBaseId }) {
    commit('SET_UI', { linking: true });
    try {
      await KnowledgeBaseAPI.unlink({ assistantId, knowledgeBaseId });
      const updated = (s.linkedByAssistant[assistantId] || []).filter(
        kb => kb.id !== knowledgeBaseId
      );
      commit('SET_LINKED', { assistantId, list: updated });
    } finally {
      commit('SET_UI', { linking: false });
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
